import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Loader2, Plus, Trash2, Search, UserCog, RotateCcw, X, KeyRound, BarChart3, Calendar } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import AdminNav from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(s) {
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

const EMPTY_FORM = { name: "", username: "", password: "", course: "CMA USA", phone: "" };

function CreateStudentDialog({ open, onClose, onCreated, authHeader }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(EMPTY_FORM); }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.username.trim() || !form.password.trim()) {
      toast.error("Name, username and password are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, username: form.username.trim().toLowerCase() };
      const { data } = await axios.post(`${API}/admin/students`, payload, { headers: authHeader });
      toast.success(`Student "${data.name}" created`);
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create student");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg" data-testid="create-student-dialog">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="font-rubik text-xl font-bold text-[#0f172a]">Create Student Login</h2>
            <p className="font-inter text-xs text-slate-500 mt-1">Share these credentials with the student.</p>
          </div>
          <button onClick={onClose} data-testid="create-student-close-btn" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {[
            { name: "name", label: "Full Name", placeholder: "Aiswarya Menon", required: true },
            { name: "username", label: "Username", placeholder: "aiswarya.menon", required: true, autoComplete: "off" },
            { name: "password", label: "Password", placeholder: "Set a temporary password", type: "text", required: true, autoComplete: "new-password" },
            { name: "course", label: "Course", placeholder: "CMA USA / ACCA / CA …" },
            { name: "phone", label: "Phone (optional)", placeholder: "+91 …" },
          ].map((f) => (
            <div key={f.name}>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor={`field-${f.name}`}>{f.label}</label>
              <input
                id={`field-${f.name}`}
                data-testid={`student-${f.name}-input`}
                type={f.type || "text"}
                value={form[f.name]}
                onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                placeholder={f.placeholder}
                required={!!f.required}
                autoComplete={f.autoComplete || "off"}
                className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={saving}
            data-testid="create-student-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3.5 font-inter font-medium transition-all"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <>+ Create Student</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function ResultsDrawer({ student, results, onClose }) {
  const [expandedId, setExpandedId] = useState(null);
  if (!student) return null;

  const SECTION_NAMES = {
    P2: { A: "Financial Statement Analysis", B: "Corporate Finance", C: "Decision Analysis", D: "Risk Management", E: "Investment Decisions", F: "Professional Ethics", M: "Case Studies" },
    P1: { A: "External Financial Reporting", B: "Planning & Budgeting", C: "Performance Management", D: "Cost Management", E: "Internal Controls", F: "Technology & Analytics", M: "Case Studies" },
  };
  const labelFor = (part, sid) => (SECTION_NAMES[part] && SECTION_NAMES[part][sid]) || (sid === "M" ? "Case Studies" : `Section ${sid}`);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="results-drawer">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <p className="font-inter text-xs font-semibold uppercase tracking-wider text-[#002570]">Exam Attempts</p>
            <h2 className="font-rubik text-xl font-bold text-[#0f172a]">{student.name} · {student.username}</h2>
          </div>
          <button onClick={onClose} data-testid="results-drawer-close-btn" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1">
          {results.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#73c3ff]/15 text-[#002570] flex items-center justify-center mb-4"><BarChart3 className="w-6 h-6" /></div>
              <p className="font-rubik text-lg font-semibold text-[#0f172a]">No attempts yet</p>
              <p className="mt-1 font-inter text-sm text-slate-500">This student hasn't taken any mock exams.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {results.map((r) => {
                const isOpen = expandedId === r.id;
                const sectionEntries = r.section_stats ? Object.entries(r.section_stats).sort(([a], [b]) => a.localeCompare(b)) : [];
                const focusAreas = r.focus_areas || [];
                return (
                  <div key={r.id} className="p-5" data-testid={`result-card-${r.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center text-white font-bold ${r.percentage >= 72 ? "bg-emerald-600" : r.percentage >= 50 ? "bg-amber-500" : "bg-rose-600"}`}>
                          <span className="text-base leading-none">{r.score}</span>
                          <span className="text-[9px] uppercase tracking-wider opacity-90 mt-0.5">/{r.max_score}</span>
                        </div>
                        <div>
                          <p className="font-rubik text-base font-bold text-[#0f172a]">{r.exam_title}{r.part ? <span className="ml-2 inline-block bg-[#73c3ff]/15 text-[#002570] text-[10px] font-inter font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">Part {r.part}</span> : null}</p>
                          <p className="font-inter text-xs text-slate-500 mt-0.5">
                            <Calendar className="w-3 h-3 inline mr-1" />{formatDate(r.created_at)} · {r.time_used || "—"}{r.auto_submitted ? " (auto-submitted)" : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-block text-xs font-inter font-semibold px-3 py-1.5 rounded-full ${r.percentage >= 72 ? "bg-emerald-50 text-emerald-700" : r.percentage >= 50 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{r.percentage.toFixed(1)}%</span>
                        <button
                          onClick={() => setExpandedId(isOpen ? null : r.id)}
                          data-testid={`result-toggle-${r.id}`}
                          className="text-xs font-inter font-semibold text-[#002570] hover:text-[#001a52] underline-offset-4 hover:underline"
                        >
                          {isOpen ? "Hide details" : "View details"}
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="mt-5 grid md:grid-cols-2 gap-5">
                        <div className="bg-[#f8fafc] rounded-2xl p-4">
                          <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Score breakdown</p>
                          <div className="space-y-2 font-inter text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">MCQ phase</span><b className="text-[#0f172a]">{r.mcq_marks} / 400</b></div>
                            <div className="flex justify-between"><span className="text-slate-600">Case studies</span><b className="text-[#0f172a]">{r.case_marks} / 100</b></div>
                            <div className="flex justify-between border-t border-slate-200 pt-2"><span className="text-slate-700 font-semibold">Total</span><b className="text-[#002570]">{r.score} / {r.max_score}</b></div>
                            <div className="flex justify-between"><span className="text-slate-600">Questions correct</span><b className="text-[#0f172a]">{r.correct_count} / {r.total_questions}</b></div>
                            <div className="flex justify-between"><span className="text-slate-600">Time used</span><b className="text-[#0f172a]">{r.time_used || "—"}</b></div>
                          </div>
                        </div>

                        <div className="bg-[#f8fafc] rounded-2xl p-4">
                          <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Performance by section</p>
                          {sectionEntries.length === 0 ? (
                            <p className="font-inter text-xs text-slate-400 italic">No section breakdown captured for this attempt.</p>
                          ) : (
                            <div className="space-y-2.5">
                              {sectionEntries.map(([sid, s]) => {
                                const pct = s.total ? Math.round((s.correct / s.total) * 100) : 0;
                                const barColor = pct < 50 ? "bg-rose-500" : pct < 75 ? "bg-amber-500" : "bg-emerald-500";
                                return (
                                  <div key={sid}>
                                    <div className="flex justify-between text-xs font-inter mb-1">
                                      <span className="text-slate-700 font-semibold">{sid === "M" ? "Case Studies" : `Section ${sid}`}<span className="text-slate-400 font-normal"> · {labelFor(r.part, sid)}</span></span>
                                      <span className="text-slate-600">{s.correct}/{s.total} ({pct}%)</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                      <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {focusAreas.length > 0 && (
                          <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-4 md:col-span-2">
                            <p className="font-inter text-xs font-semibold uppercase tracking-wider text-rose-700 mb-3">Focus areas (most missed topics)</p>
                            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 font-inter text-sm">
                              {focusAreas.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-slate-700">
                                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                                  <span><b className="text-[#0f172a]">{f.topic}</b> <span className="text-slate-500">— {f.secName || f.section} ({f.errs} wrong)</span></span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ student, onClose, authHeader }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  if (!student) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/students/${student.id}`, { password: pwd }, { headers: authHeader });
      toast.success(`Password reset for ${student.username}`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-md" data-testid="reset-pwd-dialog">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-rubik text-xl font-bold text-[#0f172a]">Reset password — {student.username}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="reset-pwd-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <input
            type="text"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="New password"
            data-testid="reset-pwd-input"
            className="w-full bg-white rounded-xl border border-slate-200 px-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff]"
          />
          <button type="submit" disabled={saving} data-testid="reset-pwd-submit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3.5 font-inter font-medium transition-all">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminStudents() {
  const { user, authHeader } = useAuth();
  const [students, setStudents] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [pwdStudent, setPwdStudent] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/students`, { headers: authHeader }),
        axios.get(`${API}/admin/exam-results`, { headers: authHeader }),
      ]);
      setStudents(s.data || []);
      setAllResults(r.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load students");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return students.filter((s) => !q || s.name.toLowerCase().includes(q) || s.username.toLowerCase().includes(q) || (s.course || "").toLowerCase().includes(q));
  }, [students, query]);

  const resultsByStudent = useMemo(() => {
    const m = {};
    allResults.forEach((r) => {
      if (!m[r.student_id]) m[r.student_id] = [];
      m[r.student_id].push(r);
    });
    return m;
  }, [allResults]);

  const onDelete = async (s) => {
    if (!window.confirm(`Delete student "${s.name}" and ALL their exam results permanently?`)) return;
    setDeletingId(s.id);
    try {
      await axios.delete(`${API}/admin/students/${s.id}`, { headers: authHeader });
      setStudents((prev) => prev.filter((x) => x.id !== s.id));
      setAllResults((prev) => prev.filter((x) => x.student_id !== s.id));
      toast.success("Student removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const onResetAttempts = async (s) => {
    if (!window.confirm(`Reset attempts for "${s.name}" back to 0?`)) return;
    setResettingId(s.id);
    try {
      await axios.post(`${API}/admin/students/${s.id}/reset-attempts`, {}, { headers: authHeader });
      setStudents((prev) => prev.map((x) => x.id === s.id ? { ...x, attempts_used: 0 } : x));
      toast.success("Attempts reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setResettingId(null);
    }
  };

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]"><Loader2 className="w-8 h-8 animate-spin text-[#002570]" /></div>;
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <AdminNav />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Admin Dashboard</p>
            <h1 className="mt-2 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a]">Students & Mock Exam Tracking</h1>
            <p className="mt-2 font-inter text-sm text-slate-600">Issue student logins, view every attempt, and reset progress when needed.</p>
          </div>
          <button onClick={() => setOpenCreate(true)} data-testid="add-student-btn" className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-5 py-3 text-sm font-inter font-medium transition-colors">
            <Plus className="w-4 h-4" /> Add Student
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total Students", value: students.length },
            { label: "Total Attempts", value: allResults.length },
            { label: "Avg Score", value: allResults.length ? `${(allResults.reduce((a, r) => a + (r.percentage || 0), 0) / allResults.length).toFixed(1)}%` : "—" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">{s.label}</p>
              <p className="mt-1 font-rubik text-2xl font-bold text-[#0f172a]">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 mb-6">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="students-search-input"
              placeholder="Search by name, username or course…"
              className="w-full bg-[#f8fafc] rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 px-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#73c3ff]/15 text-[#002570] flex items-center justify-center mb-4"><UserCog className="w-6 h-6" /></div>
              <p className="font-rubik text-lg font-semibold text-[#0f172a]">No students yet</p>
              <p className="mt-1 font-inter text-sm text-slate-500">Create the first student login to give access to the mock exam portal.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="students-table">
                <thead className="bg-[#f8fafc] border-b border-slate-200">
                  <tr>
                    {["Name", "Username", "Course", "Phone", "Attempts", "Best %", "Created", "Actions"].map((h) => (
                      <th key={h} className="text-left font-inter text-xs font-semibold uppercase tracking-wider text-slate-500 px-4 md:px-5 py-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const rs = resultsByStudent[s.id] || [];
                    const best = rs.length ? Math.max(...rs.map((r) => r.percentage || 0)).toFixed(1) + "%" : "—";
                    return (
                      <tr key={s.id} data-testid={`student-row-${s.id}`} className="border-b border-slate-100 hover:bg-[#f8fafc] transition-colors">
                        <td className="px-4 md:px-5 py-4 font-rubik font-semibold text-[#0f172a] whitespace-nowrap">{s.name}</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-sm text-slate-700 whitespace-nowrap">{s.username}</td>
                        <td className="px-4 md:px-5 py-4">
                          {s.course ? <span className="inline-block bg-[#73c3ff]/15 text-[#002570] text-xs font-inter font-medium px-3 py-1.5 rounded-full whitespace-nowrap">{s.course}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 md:px-5 py-4 font-inter text-sm text-slate-600 whitespace-nowrap">{s.phone || "—"}</td>
                        <td className="px-4 md:px-5 py-4">
                          <button
                            onClick={() => setViewingStudent(s)}
                            data-testid={`student-attempts-${s.id}`}
                            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-full font-inter text-xs font-medium transition-colors"
                          >
                            <BarChart3 className="w-3.5 h-3.5" /> {rs.length} attempts
                          </button>
                        </td>
                        <td className="px-4 md:px-5 py-4 font-rubik text-sm font-bold text-[#002570] whitespace-nowrap">{best}</td>
                        <td className="px-4 md:px-5 py-4 font-inter text-xs text-slate-500 whitespace-nowrap">{formatDate(s.created_at)}</td>
                        <td className="px-4 md:px-5 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setPwdStudent(s)}
                              data-testid={`student-resetpwd-${s.id}`}
                              className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-500 hover:text-white flex items-center justify-center transition-colors"
                              aria-label="Reset password"
                              title="Reset password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onResetAttempts(s)}
                              disabled={resettingId === s.id}
                              data-testid={`student-resetattempts-${s.id}`}
                              className="w-9 h-9 rounded-full bg-[#002570]/10 text-[#002570] hover:bg-[#002570] hover:text-white flex items-center justify-center transition-colors disabled:opacity-60"
                              aria-label="Reset attempts"
                              title="Reset attempt counter"
                            >
                              {resettingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => onDelete(s)}
                              disabled={deletingId === s.id}
                              data-testid={`student-delete-${s.id}`}
                              className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center transition-colors disabled:opacity-60"
                              aria-label="Delete"
                              title="Delete student"
                            >
                              {deletingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-6 font-inter text-xs text-slate-400 text-center">Showing {filtered.length} of {students.length} students</p>
      </main>

      <CreateStudentDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(s) => setStudents((prev) => [s, ...prev])}
        authHeader={authHeader}
      />
      <ResultsDrawer
        student={viewingStudent}
        results={viewingStudent ? (resultsByStudent[viewingStudent.id] || []) : []}
        onClose={() => setViewingStudent(null)}
      />
      <ResetPasswordDialog
        student={pwdStudent}
        onClose={() => setPwdStudent(null)}
        authHeader={authHeader}
      />
    </div>
  );
}
